import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi/useApi";
import type { UpsertUserPermissionData } from "~pages/Users/PermissionUserManagePage/types";
import type { UserTransformed } from "~pages/Users/types";
import { transformUserPermission } from "~pages/Users/utilities";
import type { UserPermissionResponse } from "~types/userPermission";

const useUpsertUserPermission = () => {
  const { sport, organizationId, userId } = useParams();
  const request = useApi();
  const queryClient = useQueryClient();

  return useMutation<UserPermissionResponse, Error, UpsertUserPermissionData>({
    mutationFn: async (upsertModel: UpsertUserPermissionData) => {
      let url = `${sport}/o/${organizationId}/users/${userId}/permissions`;
      if (upsertModel.permissionId) {
        url += `/${upsertModel.permissionId}`;
      }
      const method = upsertModel.permissionId ? "PUT" : "POST";
      const body = upsertModel.upsertData;
      return await request(url, { method, body });
    },
    onSuccess: async (response: UserPermissionResponse, upsertModel: UpsertUserPermissionData) => {
      if (response?.data?.length > 0) {
        const existingUser: UserTransformed | undefined = queryClient.getQueryData(["permission-users", sport, organizationId, userId]);

        if (existingUser) {
          const responseResources = response.includes?.resources || {};
          const upsertedPermission = transformUserPermission(response.data[0], false, responseResources);
          if (upsertModel.permissionId) {
            existingUser.permissions = existingUser.permissions.map((permission) => {
              if (permission.permissionId === upsertModel.permissionId) {
                return upsertedPermission;
              }
              return permission;
            });
          } else {
            existingUser.permissions.unshift(upsertedPermission);
          }
        }
      }
    },
  });
};

export default useUpsertUserPermission;
